import React from 'react';
import { Box, Typography, LinearProgress, Paper, Skeleton, useTheme } from '@mui/material';
import { keyframes } from '@mui/system';
import { C } from '../themes';

/**
 * THE BRAND MARK, BREATHING — the app's loader.
 *
 * It replaces a Material CircularProgress, which is the same spinner every
 * unfinished web app shows: it said "something is happening" and nothing about
 * whose product you were waiting on. A logo that breathes says both, and the
 * two clients now wait in the same way — the phone runs this same zoom on the
 * same mark.
 *
 * ZOOM, NOT SPIN. A spinner rotating at a constant rate reads as a machine; a
 * slow scale in and out reads as breathing, and at 1.9s it is calm enough to
 * watch for the four or five seconds a slow connection makes someone watch it.
 * The soft gold halo behind it scales on the same clock, half a beat wider, so
 * the mark looks lit rather than merely resized.
 *
 * `prefers-reduced-motion` stops the zoom outright — a pulsing element is one
 * of the specific things that setting exists to turn off — and leaves the halo
 * fading gently, so there is still a sign of life.
 */
export function BrandLoader({ size = 72, label }) {
  const theme = useTheme();

  const breathe = keyframes`
    0%   { transform: scale(0.82); opacity: 0.75; }
    50%  { transform: scale(1.06); opacity: 1; }
    100% { transform: scale(0.82); opacity: 0.75; }
  `;
  const halo = keyframes`
    0%   { transform: scale(0.7);  opacity: 0.10; }
    50%  { transform: scale(1.18); opacity: 0.34; }
    100% { transform: scale(0.7);  opacity: 0.10; }
  `;
  const fade = keyframes`
    0%, 100% { opacity: 0.45; }
    50%      { opacity: 0.9; }
  `;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Box sx={{ position: 'relative', width: size * 1.8, height: size * 1.8,
        display: 'grid', placeItems: 'center' }}>
        <Box
          aria-hidden
          sx={{
            position: 'absolute', width: size * 1.7, height: size * 1.7, borderRadius: '50%',
            background: `radial-gradient(circle, ${C.gold[300]} 0%, ${C.royal[100]} 45%, transparent 70%)`,
            animation: `${halo} 1.9s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': {
              animation: `${fade} 2.4s ease-in-out infinite`,
            },
          }}
        />
        <Box
          component="img"
          src="/encatarlogo.png"
          alt=""
          sx={{
            width: size, height: size, objectFit: 'contain', position: 'relative',
            animation: `${breathe} 1.9s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1 },
          }}
        />
      </Box>
      {/* The live region is the label, not the image: a screen reader needs to
          be told the page is loading, and an animated logo tells it nothing. */}
      <Typography
        role="status"
        aria-live="polite"
        variant="body2"
        sx={{ color: theme.palette.text.secondary }}
      >
        {label || ''}
      </Typography>
    </Box>
  );
}

// Centralized, elegant inline loader using skeleton shimmer (no modal overlay)
// Usage:
// <AppLoader message="Loading dashboard..." />
// <AppLoader variant="table" rows={6} />
// <AppLoader linear message="Refreshing..." height={3} dense />

export default function AppLoader({
  message = 'Loading... Please wait',
  variant = 'cards', // 'cards' | 'table' | 'circular3'
  rows = 3,
  linear = false,
  height = 4,
  dense = false,
  messages = [], // only for circular3; array of 3 strings
  size = 56,
  centered = true,
  minHeight = '50vh',
}) {
  const theme = useTheme();
  const pulse = keyframes`
    0% { opacity: .4; }
    50% { opacity: 1; }
    100% { opacity: .4; }
  `;
  const slide = keyframes`
    0% { transform: translateX(-4px); opacity: .7; }
    50% { transform: translateX(4px); opacity: 1; }
    100% { transform: translateX(-4px); opacity: .7; }
  `;
  const fadeInUp = keyframes`
    0% { opacity: 0; transform: translateY(6px); }
    100% { opacity: 1; transform: translateY(0); }
  `;

  if (linear) {
    return (
      <Box sx={{ width: '100%', py: dense ? 0 : 1 }}>
        <LinearProgress sx={{ height, borderRadius: height / 2 }} />
        {message && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {message}
          </Typography>
        )}
      </Box>
    );
  }

  // `circular3` is what thirty pages ask for, and it is the brand mark now
  // rather than a spinner. The name is kept so none of them have to change —
  // renaming it would be thirty edits to say the same thing.
  if (variant === 'circular3') {
    return (
      <Box
        sx={{
          py: 2,
          display: 'flex',
          // CENTRED ON BOTH AXES. It was centred horizontally and pinned to the
          // top of a 50vh box, so on a tall page the mark sat well above the
          // middle of the space it was filling.
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: centered ? minHeight : 'auto',
        }}
      >
        <BrandLoader size={size} label={messages[0] || ''} />
      </Box>
    );
  }

  if (variant === 'table') {
    return (
      <Box sx={{ py: 2 }}>
        {message && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {message}
          </Typography>
        )}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Skeleton variant="rounded" height={36} sx={{ mb: 1, borderRadius: 1 }} />
          {Array.from({ length: rows }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={28} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  // Default cards/grid placeholders
  return (
    <Box sx={{ py: 2 }}>
      {message && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {message}
        </Typography>
      )}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' } }}>
        {Array.from({ length: rows }).map((_, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Skeleton variant="text" width="60%" height={28} />
            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="rounded" height={100} sx={{ mt: 1, borderRadius: 1 }} />
          </Paper>
        ))}
      </Box>
    </Box>
  );
}
